import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { ListPreferencesClient } from './list-preferences-client.service';
import { Observable, of } from 'rxjs';

const PREFERENCES_KEY = '_mp';
const COLUMNS = '_c';
const ORDER_BY = '_o';

export declare type ListPreferences = {
  id: number;
  columns: number[];
  orderBy: number;
  pageSize?: number;
};

@Injectable()
export class UserPreferencesService {
  constructor(
    private _client: ListPreferencesClient,
    private _storage: StorageService
  ) {}

  private readonly _listCache: Map<number, ListPreferences> = new Map();
  private readonly _listRequests: Map<number, Observable<ListPreferences>> = new Map();
  private readonly _setRequests: Map<string, Observable<void>> = new Map();

  getListPreferences(id: number): Observable<ListPreferences> {
    if (this._listCache.has(id)) {
      const pref = this._listCache.get(id);
      return of({ ...pref });
    }

    if (this._listRequests.has(id)) {
      return this._listRequests.get(id);
    }

    const request = this._client.get(id).pipe(
      map(res => {
        if (res) {
          const pref = { id, columns: res.columns ?? [], orderBy: res.orderBy ?? 0, pageSize: res.pageSize };
          this._listCache.set(id, pref);
          return pref;
        }
        return { id: id, columns: [], orderBy: 0 };
      }),
      finalize(() => this._listRequests.delete(id)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this._listRequests.set(id, request);
    return request;
  }

  setListPreferences(preferences: ListPreferences): Observable<void> {
    const requestKey = `${preferences.id}:${preferences.columns.join(',')}:${preferences.orderBy}`;
    if (this._setRequests.has(requestKey)) {
      return this._setRequests.get(requestKey);
    }

    const updateCache = () => {
      this._listCache.set(preferences.id, {
        ...preferences,
        pageSize: this._listCache.get(preferences.id)?.pageSize ?? preferences.pageSize,
      });
    };

    let request: Observable<void>;
    if (this._listCache.has(preferences.id)) {
      request = this._client.update(preferences).pipe(tap(updateCache));
    } else {
      request = this._client.create(preferences).pipe(tap(updateCache));
    }

    request = request.pipe(
      finalize(() => this._setRequests.delete(requestKey)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this._setRequests.set(requestKey, request);
    return request;
  }

  setListPageSize(id: number, pageSize: number): Observable<void> {
    return this._client.updatePageSize(id, pageSize).pipe(
      tap(() => {
        const pref = this._listCache.get(id);
        if (pref) this._listCache.set(id, { ...pref, pageSize });
      })
    );
  }
}
